import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";

export type SectionProps = HTMLAttributes<HTMLElement> & {
  children?: ReactNode;
  eyebrow?: ReactNode;
  title?: ReactNode;
  width?: "wide" | "editorial" | "full";
  tone?: "page" | "card" | "inset" | "ink";
  /** Drop the vertical rhythm padding. */
  flush?: boolean;
  /** Override the header block — bands vary their title size and bottom margin. */
  headerClassName?: string;
  titleClassName?: string;
};

const tones = {
  page: "bg-surface-page text-text-primary",
  card: "bg-surface-card text-text-primary",
  inset: "bg-surface-inset text-text-primary",
  ink: "bg-surface-ink text-on-ink",
} as const;

const widths = {
  wide: "max-w-band",
  editorial: "max-w-editorial",
  full: "max-w-none",
} as const;

/**
 * The gutter owner.
 *
 * This component exists specifically to prevent the defect the design system
 * was built to fix: the source component sat at a bare 16px inset, so copy ran
 * to the viewport edge. Every band goes through here.
 *
 * Note the nesting order — the gutter lives on the OUTER element and the
 * max-width on the INNER one. Reversing them insets content by an extra gutter
 * once the viewport exceeds the container width, which silently misaligns the
 * band against every other band on the page.
 */
export function Section({
  children,
  eyebrow,
  title,
  width = "wide",
  tone = "page",
  flush = false,
  className,
  headerClassName,
  titleClassName,
  ...rest
}: SectionProps) {
  return (
    <section className={cx(tones[tone], "px-gutter", flush ? "py-0" : "py-section-y", className)} {...rest}>
      <div className={cx("mx-auto w-full", widths[width])}>
        {(eyebrow || title) && (
          <header className={cx("flex flex-col gap-5 mb-8", headerClassName)}>
            {eyebrow}
            {title && (
              <h2 className={cx("font-display font-regular text-display-3 max-w-[22ch]", titleClassName)}>{title}</h2>
            )}
          </header>
        )}
        {children}
      </div>
    </section>
  );
}
